import * as React from "react";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Grid,
  Typography,
  CardContent,
  IconButton,
  Modal,
  Stack,
} from "@mui/material";
import Card from "@mui/material/Card";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import { ConfirmDialog } from "primereact/confirmdialog";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api, { HOST_URL3 } from "../../utils/Url";
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import * as signalR from "@microsoft/signalr";

export default function VisitorDashboard() {
  const { t } = useTranslation();
  const [visitorData, setVisitorData] = useState<any[]>([]);
  const [visitorHistory, setVisitorHistory] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<any>(null);
  const [stillInsideCount, setStillInsideCount] = useState<number>(0);
  const [visitorsCount, setVisitorsCount] = useState<number>(0);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

  // Function to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer | null) => {
    if (!buffer || buffer.byteLength === 0) return "";
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  // Initialize SignalR connection
  // useEffect(() => {
  //   const newConnection = new signalR.HubConnectionBuilder()
  //     .withUrl("https://realtimedashboardcsjmu.mssplonline.com/visitorHub", {
  //       skipNegotiation: true,
  //       transport: signalR.HttpTransportType.WebSockets
  //     })
  //     .configureLogging(signalR.LogLevel.Information)
  //     .build();

  //   setConnection(newConnection);
  // }, []);

  // Start SignalR connection and set up listeners
  useEffect(() => {
    if (connection) {
      connection.start()
        .then(() => {
          console.log("SignalR Connected");

          // Set up listeners
          connection.on("ReceiveVisitorUpdate", (visitors: any[]) => {
            // Filter out empty entries and take last 3 valid visitors
            const validVisitors = visitors
              .filter((visitor: any) => visitor.name && visitor.memCd)
              .slice(-3);
            setVisitorData(validVisitors);
          });

          connection.on("ReceiveStillInsideUpdate", (visitors: any[]) => {
            setVisitorHistory(visitors);
          });

          connection.on("ReceiveCountUpdate", (counts: any) => {
            setStillInsideCount(counts.stillInside);
            setVisitorsCount(counts.totalVisitors);
          });
        })
        .catch(err => console.error("SignalR Connection Error: ", err));

      return () => {
        connection.stop();
      };
    }
  }, [connection]);

  // Fallback to API calls if SignalR fails
  const fetchCounts = async () => {
    try {
      const response = await api.post("AssigningRfid/CountTodayVisit");
      setVisitorsCount(response.data.data[0].visitorCount);
      const response1 = await api.post("AssigningRfid/CountTodayStillInside");
      setStillInsideCount(response1.data.data[0].visitorCount);
    } catch (error) {
      console.error("Error fetching Data", error);
    }
  }

  // Fallback to API calls if SignalR fails
  const fetchRecentVisitors = async () => {
    try {
      const response = await axios.get(
        `${HOST_URL3}visitors/recent`
      );
      // Filter out empty entries and take last 3 valid visitors
      const validVisitors = response.data
        .filter((visitor: any) => visitor.name && visitor.memCd)
        .slice(-3);
      setVisitorData(validVisitors);
    } catch (error) {
      console.error("Error fetching recent visitors:", error);
      // toast.error("Failed to load recent visitors");
    }
  };

  // Fallback to API calls if SignalR fails
  const fetchVisitorsInside = async () => {
    try {
      const date = moment(new Date()).format("YYYY-MM-DD");
      const response = await axios.get(
        `${HOST_URL3}visitors/still-inside?vdate=${date}&todate=${date}`
      );
      setVisitorHistory(response.data);
    } catch (error) {
      console.error("Error fetching visitors inside:", error);
      // toast.error("Failed to load visitor history");
    }
  };

  // Set up fallback polling if SignalR fails
  useEffect(() => {
    // Initial fetch
    if (!connection) {
      fetchRecentVisitors();
      fetchVisitorsInside();
      fetchCounts();
    }

    // Set up interval for fallback polling (every 5 seconds)
    const intervalId = setInterval(() => {
      if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
        fetchRecentVisitors();
        fetchVisitorsInside();
        fetchCounts();
      }
    }, 2500);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [connection]);

  // Rest of your component remains exactly the same...
  const handleOpenModal = (row: any) => {
    setSelectedVisitor(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedVisitor(null);
  };

  const columns: GridColDef[] = [
    {
      field: "memCd",
      headerName: "Member ID",
      flex: 1,
      headerClassName: "header-theme",
      minWidth: 120,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      headerClassName: "header-theme",
      minWidth: 150,
    },
    {
      field: "vDate",
      headerName: "Visit Date",
      flex: 1,
      headerClassName: "header-theme",
      minWidth: 120,
      valueFormatter: (params) => moment(params.value).format("DD/MM/YYYY"),
    },
    {
      field: "inTime",
      headerName: "In Time",
      flex: 0.8,
      headerClassName: "header-theme",
      minWidth: 100,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("hh:mm A") : "N/A",
    },
    {
      field: "outTime",
      headerName: "Out Time",
      flex: 0.8,
      headerClassName: "header-theme",
      minWidth: 100,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("hh:mm A") : "N/A",
    },
    {
      field: "memDept",
      headerName: "Department",
      flex: 1.2,
      headerClassName: "header-theme",
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      headerClassName: "header-theme",
      minWidth: 100,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === "OUT" ? "#E9FDEE" : "#FFF4E6",
            color: params.value === "OUT" ? "#059669" : "#F97316",
            padding: "4px 8px",
            borderRadius: "12px",
            fontWeight: 500,
            fontSize: "0.75rem",
            width: "100%",
            textAlign: "center",
          }}
        >
          {params.value || "IN"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "header-theme",
      type: "actions",
      flex: 0.5,
      minWidth: 80,
      getActions: (params) => [
        <IconButton
          color="primary"
          onClick={() => handleOpenModal(params.row)}
        >
          <VisibilityIcon />
        </IconButton>,
      ],
    },
  ];

  const renderPhoto = (memberPhoto: string | null, size = 120) => {
    if (!memberPhoto) {
      return (
        <Avatar
          sx={{
            width: size,
            height: size,
            border: "3px solid #2B4593",
          }}
        >
          <Typography variant="h6">No Photo</Typography>
        </Avatar>
      );
    }

    return (
      <Avatar
        src={`data:image/jpeg;base64,${memberPhoto}`}
        sx={{
          width: size,
          height: size,
          border: "3px solid #2B4593",
        }}
      />
    );
  };

  const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: 800,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: "90vh",
    overflowY: "auto",
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        marginTop: "4px",
        padding: "8px",
        "& .header-theme": {
          backgroundColor: "#2B4593",
          color: "#fff",
          fontSize: 16,
          fontWeight: 600,
          background: "#1E2F6D"
        },
      }}
    >
      <ConfirmDialog />
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        mb: 2,
        padding: '12px 0',
        // background: 'linear-gradient(to right, #f5f7fa 0%, #e4e8f0 100%)',
        background: 'linear-gradient(to right,rgb(80, 220, 255),rgb(169, 228, 255))',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: 'bold',
            color: '#2B4593',
            pl: 2
          }}
        >
          Realtime Visitor Dashboard
        </Typography>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: 'red', fontWeight: 'bold', fontSize: "1.3rem" }}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '9999px',
                marginRight: '4px',
                backgroundColor: '#005f00',
                animation: 'pulse 1.5s infinite',
              }}
            ></span>
            Live
          </span>
          <style>
            {`
              @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.4;
                transform: scale(1.2);
              }
              }
           `}
          </style>
        </div>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            pr: 2,
            pl: 2,
            '& span:first-of-type': {
              color: 'blueviolet'
            },
            '& span:last-of-type': {
              color: 'brown'
            }
          }}
        >
          <span>Today Still Inside: {stillInsideCount}</span>
          {' | '}
          <span>Today Visitors: {visitorsCount}</span>
        </Typography>
      </Box>
      <Divider />

      <Box height={8} />
      {/* <ToastContainer /> */}

      <Box sx={{ padding: "12px" }}>
        {visitorData.length > 0 ? (
          <Grid container spacing={3}>
            {visitorData.map((visitor, index) => (
              <Grid item xs={12} sm={6} md={4} key={visitor.memCd || visitor.epc}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid black",
                    borderRadius: "12px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    background: "linear-gradient(to bottom right, #f8fafc 0%, #f1f5f9 100%)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                    },
                    animation: `slideIn 0.5s ease-out ${index * 0.1}s forwards`,
                    opacity: 0,
                    transform: "translateX(20px)",
                    "@keyframes slideIn": {
                      "100%": {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "16px 0",
                      // background: "linear-gradient(to right, #f0f4f8 0%, #e2e8f0 100%)",
                      background: "linear-gradient(to right, #3a7bd5 0%, #3a6073 100%)",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    {renderPhoto(visitor.memberPhoto, 140)}
                  </Box>

                  <CardContent sx={{ flexGrow: 1, padding: "16px" }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: "bold",
                        color: "#2B4593",
                        mb: 2,
                        textAlign: "center"
                      }}
                    >
                      {visitor.name}
                    </Typography>

                    <Stack spacing={1}>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Member ID:</strong> {visitor.memCd || "N/A"}
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Mobile:</strong> {visitor.mobileNo || "N/A"}
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Department:</strong> {visitor.memDept || "N/A"}
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Date:</strong> {moment(visitor.vDate).format("DD MMM YYYY")}
                      </Typography>

                      {visitor.inTime && (<Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>In Time:</strong> {visitor.inTime ? moment(visitor.inTime).format("hh:mm A") : "N/A"}
                      </Typography>)}

                      {visitor.outTime && (<Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Out Time:</strong> {visitor.outTime ? moment(visitor.outTime).format("hh:mm A") : "N/A"}
                      </Typography>)}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 2,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor:
                              visitor.status === "OUT" ? "#E9FDEE" : "#FFF4E6",
                            color:
                              visitor.status === "OUT" ? "#059669" : "#F97316",
                            padding: "6px 16px",
                            borderRadius: "12px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            width: "fit-content",
                          }}
                        >
                          {visitor.status || "IN"}
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary" align="center">
            No recent visitor data available
          </Typography>
        )}
      </Box>

      <Box sx={{ padding: "12px" }}>
        <Card
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <CardContent sx={{ padding: "16px" }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="#2B4593"
              gutterBottom
              align="center"
            >
              Current Visitors Inside
            </Typography>
            <Divider sx={{ marginBottom: "16px" }} />

            <Box sx={{
              height: "auto",
              minHeight: 400,
              maxHeight: 500,
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-main": {
                height: "100%",
              },
              "& .MuiDataGrid-virtualScroller": {
                minHeight: 200,
                overflowY: "auto",
              },
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "2px solid #2B4593",
              },
              "& .MuiDataGrid-cell": {
                borderRight: "1px solid #f0f0f0",
              },
            }}>
              <DataGrid
                rows={visitorHistory}
                columns={columns}
                autoHeight={false}
                slots={{
                  toolbar: GridToolbar,
                }}
                rowSpacingType="border"
                pagination={true}
                pageSizeOptions={[5, 10, 25, 50]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 5
                    }
                  },
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                }}
                getRowId={(row) => row.epc || row.memCd || Math.random()}
                sx={{
                  "& .MuiDataGrid-colCell": {
                    backgroundColor: "#2B4593",
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="visitor-details-modal"
        aria-describedby="visitor-details-description"
      >
        <Box sx={modalStyle}>
          {selectedVisitor && (
            <>
              <Typography id="visitor-details-modal" variant="h6" component="h2" sx={{
                mb: 3,
                textAlign: "center",
                color: "#2B4593",
                fontWeight: "bold"
              }}>
                Visitor Details
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={4} sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center"
                }}>
                  {renderPhoto(selectedVisitor.memberPhoto, 180)}
                  <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
                    {selectedVisitor.name}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor:
                        selectedVisitor.status === "OUT" ? "#E9FDEE" : "#FFF4E6",
                      color:
                        selectedVisitor.status === "OUT" ? "#059669" : "#F97316",
                      padding: "6px 16px",
                      borderRadius: "12px",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      width: "fit-content",
                      mt: 1,
                      mb: 2
                    }}
                  >
                    {selectedVisitor.status || "IN"}
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Member ID:</strong> {selectedVisitor.memCd || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Mobile:</strong> {selectedVisitor.mobileNo || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Department:</strong> {selectedVisitor.memDept || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Type:</strong> {selectedVisitor.memType || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Visit Date:</strong> {moment(selectedVisitor.vDate).format("DD MMM YYYY")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>In Time:</strong> {selectedVisitor.inTime ? moment(selectedVisitor.inTime).format("hh:mm A") : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Out Time:</strong> {selectedVisitor.outTime ? moment(selectedVisitor.outTime).format("hh:mm A") : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Time Spent:</strong> {selectedVisitor.spendtime || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Modal>
    </Paper>
  );
}




















// import * as React from "react";
// import Paper from "@mui/material/Paper";
// import { useEffect, useState } from "react";
// import {
//   Avatar,
//   Box,
//   Divider,
//   Grid,
//   Typography,
//   CardContent,
//   IconButton,
//   Modal,
//   Stack,
// } from "@mui/material";
// import Card from "@mui/material/Card";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import { useTranslation } from "react-i18next";
// import moment from "moment";
// import { toast, ToastContainer } from "react-toastify";
// import { ConfirmDialog } from "primereact/confirmdialog";
// import axios from "axios";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import api from "../../utils/Url";

// export default function VisitorDashboard() {
//   const { t } = useTranslation();
//   const [visitorData, setVisitorData] = useState<any[]>([]);
//   const [visitorHistory, setVisitorHistory] = useState<any[]>([]);
//   const [openModal, setOpenModal] = useState(false);
//   const [selectedVisitor, setSelectedVisitor] = useState<any>(null);
//   const [stillInsideCount, setStillInsideCount] = useState<number>(0);
//   const [visitorsCount, setVisitorsCount] = useState<number>(0);


//   const fetchCounts = async () => {
//     try {
//       const response = await api.post("AssigningRfid/CountTodayVisit");
//       setVisitorsCount(response.data.data[0].visitorCount);
//       const response1 = await api.post("AssigningRfid/CountTodayStillInside");
//       setStillInsideCount(response1.data.data[0].visitorCount);

//     } catch (error) {
//       console.error("Error fetching Data", error);
//     }
//   }

//   // Function to fetch recent visitors
//   const fetchRecentVisitors = async () => {
//     try {
//       const response = await axios.get(
//         "https://realtimedashboardcsjmu.mssplonline.com/api/visitors/recent"
//       );
//       // Filter out empty entries and take last 3 valid visitors
//       const validVisitors = response.data
//         .filter((visitor: any) => visitor.name && visitor.memCd)
//         .slice(-3);
//       setVisitorData(validVisitors);
//     } catch (error) {
//       console.error("Error fetching recent visitors:", error);
//       toast.error("Failed to load recent visitors");
//     }
//   };

//   // Function to fetch visitors still inside
//   const fetchVisitorsInside = async () => {
//     try {
//       const date = moment(new Date()).format("YYYY-MM-DD");
//       const response = await axios.get(
//         `https://realtimedashboardcsjmu.mssplonline.com/api/visitors/still-inside?vdate=${date}&todate=${date}`
//       );
//       setVisitorHistory(response.data);
//     } catch (error) {
//       console.error("Error fetching visitors inside:", error);
//       toast.error("Failed to load visitor history");
//     }
//   };

//   // Set up real-time updates
//   useEffect(() => {
//     // Initial fetch
//     fetchRecentVisitors();
//     fetchVisitorsInside();
//     fetchCounts();

//     // Set up interval for real-time updates (every 5 seconds)
//     const intervalId = setInterval(() => {
//       fetchRecentVisitors();
//       fetchVisitorsInside();
//       fetchCounts();
//     }, 2000);

//     // Clean up interval on component unmount
//     return () => clearInterval(intervalId);
//   }, []);

//   // Function to handle opening visitor details modal
//   const handleOpenModal = (row: any) => {
//     setSelectedVisitor(row);
//     setOpenModal(true);
//   };

//   // Function to handle closing modal
//   const handleCloseModal = () => {
//     setOpenModal(false);
//     setSelectedVisitor(null);
//   };

//   const columns: GridColDef[] = [
//     {
//       field: "memCd",
//       headerName: "Member ID",
//       flex: 1,
//       headerClassName: "header-theme",
//       minWidth: 120,
//     },
//     {
//       field: "name",
//       headerName: "Name",
//       flex: 1,
//       headerClassName: "header-theme",
//       minWidth: 150,
//     },
//     {
//       field: "vDate",
//       headerName: "Visit Date",
//       flex: 1,
//       headerClassName: "header-theme",
//       minWidth: 120,
//       valueFormatter: (params) => moment(params.value).format("DD/MM/YYYY"),
//     },
//     {
//       field: "inTime",
//       headerName: "In Time",
//       flex: 0.8,
//       headerClassName: "header-theme",
//       minWidth: 100,
//       valueFormatter: (params) =>
//         params.value ? moment(params.value).format("hh:mm A") : "N/A",
//     },
//     {
//       field: "outTime",
//       headerName: "Out Time",
//       flex: 0.8,
//       headerClassName: "header-theme",
//       minWidth: 100,
//       valueFormatter: (params) =>
//         params.value ? moment(params.value).format("hh:mm A") : "N/A",
//     },
//     {
//       field: "memDept",
//       headerName: "Department",
//       flex: 1.2,
//       headerClassName: "header-theme",
//       minWidth: 150,
//     },
//     {
//       field: "status",
//       headerName: "Status",
//       flex: 0.8,
//       headerClassName: "header-theme",
//       minWidth: 100,
//       renderCell: (params) => (
//         <Box
//           sx={{
//             backgroundColor:
//               params.value === "OUT" ? "#E9FDEE" : "#FFF4E6",
//             color: params.value === "OUT" ? "#059669" : "#F97316",
//             padding: "4px 8px",
//             borderRadius: "12px",
//             fontWeight: 500,
//             fontSize: "0.75rem",
//             width: "100%",
//             textAlign: "center",
//           }}
//         >
//           {params.value || "IN"}
//         </Box>
//       ),
//     },
//     {
//       field: "actions",
//       headerName: "Actions",
//       headerClassName: "header-theme",
//       type: "actions",
//       flex: 0.5,
//       minWidth: 80,
//       getActions: (params) => [
//         <IconButton
//           color="primary"
//           onClick={() => handleOpenModal(params.row)}
//         >
//           <VisibilityIcon />
//         </IconButton>,
//       ],
//     },
//   ];

//   // Function to handle photo display
//   const renderPhoto = (memberPhoto: string | null, size = 120) => {
//     if (!memberPhoto) {
//       return (
//         <Avatar
//           sx={{
//             width: size,
//             height: size,
//             border: "3px solid #2B4593",
//           }}
//         >
//           <Typography variant="h6">No Photo</Typography>
//         </Avatar>
//       );
//     }

//     return (
//       <Avatar
//         src={`data:image/jpeg;base64,${memberPhoto}`}
//         sx={{
//           width: size,
//           height: size,
//           border: "3px solid #2B4593",
//         }}
//       />
//     );
//   };

//   // Modal style
//   const modalStyle = {
//     position: "absolute" as "absolute",
//     top: "50%",
//     left: "50%",
//     transform: "translate(-50%, -50%)",
//     width: "80%",
//     maxWidth: 800,
//     bgcolor: "background.paper",
//     boxShadow: 24,
//     p: 4,
//     borderRadius: 2,
//     maxHeight: "90vh",
//     overflowY: "auto",
//   };

//   return (
//     <Paper
//       sx={{
//         width: "100%",
//         overflow: "hidden",
//         marginTop: "4px",
//         padding: "8px",
//         "& .header-theme": {
//           backgroundColor: "#2B4593",
//           color: "#fff",
//           fontSize: 16,
//           fontWeight: 600,
//           background: "#1E2F6D" // "linear-gradient(135deg, #2B4593 0%, #1E2F6D 100%)",
//         },
//       }}
//     >
//       <ConfirmDialog />
//       {/* <Typography
//         gutterBottom
//         variant="h5"
//         component="div"
//         sx={{
//           padding: "12px 0",
//           textAlign: "center",
//           fontWeight: "bold",
//           color: "#2B4593",
//           background: "linear-gradient(to right, #f5f7fa 0%, #e4e8f0 100%)",
//           borderRadius: "8px",
//           boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
//           marginBottom: "16px"
//         }}
//       >
//         Realtime Visitor Dashboard
//       </Typography> */}

//       <Box sx={{
//         display: 'flex',
//         flexDirection: { xs: 'column', sm: 'row' },
//         justifyContent: 'space-between',
//         alignItems: { xs: 'flex-start', sm: 'center' },
//         gap: 2,
//         mb: 3,
//         padding: '12px 0',
//         background: 'linear-gradient(to right, #f5f7fa 0%, #e4e8f0 100%)',
//         borderRadius: '8px',
//         boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
//       }}>
//         {/* Left-aligned title */}
//         <Typography
//           variant="h5"
//           component="div"
//           sx={{
//             fontWeight: 'bold',
//             color: '#2B4593',
//             pl: 2
//           }}
//         >
//           Realtime Visitor Dashboard
//         </Typography>

//         {/* Right-aligned counts */}
//         <Typography
//           variant="h5"
//           sx={{
//             fontWeight: 'bold',
//             pr: 2,
//             '& span:first-of-type': {
//               color: 'red'
//             },
//             '& span:last-of-type': {
//               color: 'brown'
//             }
//           }}
//         >
//           <span>Today Still Inside: {stillInsideCount}</span>
//           {' | '}
//           <span>Today Visitors: {visitorsCount}</span>
//         </Typography>
//       </Box>
//       <Divider />

//       {/* <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold' }}>
//         Today Still Inside: {stillInsideCount} | Today Visitor: {visitorsCount}
//       </Typography> */}

//       <Box height={8} />
//       <ToastContainer />

//       {/* Recent Visitors Cards Section */}
//       <Box sx={{ padding: "12px" }}>
//         {visitorData.length > 0 ? (
//           <Grid container spacing={3}>
//             {visitorData.map((visitor, index) => (
//               <Grid item xs={12} sm={6} md={4} key={visitor.memCd || visitor.epc}>
//                 <Card
//                   sx={{
//                     height: "100%",
//                     display: "flex",
//                     flexDirection: "column",
//                     borderRadius: "12px",
//                     boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
//                     transition: "all 0.3s ease",
//                     background: "linear-gradient(to bottom right, #f8fafc 0%, #f1f5f9 100%)",
//                     "&:hover": {
//                       transform: "translateY(-4px)",
//                       boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
//                     },
//                     animation: `slideIn 0.5s ease-out ${index * 0.1}s forwards`,
//                     opacity: 0,
//                     transform: "translateX(20px)",
//                     "@keyframes slideIn": {
//                       "100%": {
//                         opacity: 1,
//                         transform: "translateX(0)",
//                       },
//                     },
//                   }}
//                 >
//                   {/* Image at the top */}
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "center",
//                       padding: "16px 0",
//                       background: "linear-gradient(to right, #f0f4f8 0%, #e2e8f0 100%)",
//                       borderRadius: "12px 12px 0 0",
//                     }}
//                   >
//                     {renderPhoto(visitor.memberPhoto, 140)}
//                   </Box>

//                   {/* Visitor details */}
//                   <CardContent sx={{ flexGrow: 1, padding: "16px" }}>
//                     <Typography
//                       gutterBottom
//                       variant="h6"
//                       component="div"
//                       sx={{
//                         fontWeight: "bold",
//                         color: "#2B4593",
//                         mb: 2,
//                         textAlign: "center"
//                       }}
//                     >
//                       {visitor.name}
//                     </Typography>

//                     <Stack spacing={1}>
//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>Member ID:</strong> {visitor.memCd || "N/A"}
//                       </Typography>

//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>Mobile:</strong> {visitor.mobileNo || "N/A"}
//                       </Typography>

//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>Department:</strong> {visitor.memDept || "N/A"}
//                       </Typography>

//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>Date:</strong> {moment(visitor.vDate).format("DD MMM YYYY")}
//                       </Typography>

//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>In Time:</strong> {visitor.inTime ? moment(visitor.inTime).format("hh:mm A") : "N/A"}
//                       </Typography>

//                       <Typography variant="body1" sx={{ mb: 0.5 }}>
//                         <strong>Out Time:</strong> {visitor.outTime ? moment(visitor.outTime).format("hh:mm A") : "N/A"}
//                       </Typography>

//                       <Box
//                         sx={{
//                           display: "flex",
//                           justifyContent: "center",
//                           mt: 2,
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             backgroundColor:
//                               visitor.status === "OUT" ? "#E9FDEE" : "#FFF4E6",
//                             color:
//                               visitor.status === "OUT" ? "#059669" : "#F97316",
//                             padding: "6px 16px",
//                             borderRadius: "12px",
//                             fontWeight: 600,
//                             fontSize: "0.875rem",
//                             width: "fit-content",
//                           }}
//                         >
//                           {visitor.status || "IN"}
//                         </Box>
//                       </Box>
//                     </Stack>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))}
//           </Grid>
//         ) : (
//           <Typography variant="body2" color="textSecondary" align="center">
//             No recent visitor data available
//           </Typography>
//         )}
//       </Box>

//       {/* Current Visitors Section */}
//       <Box sx={{ padding: "12px" }}>
//         <Card
//           sx={{
//             backgroundColor: "#FFFFFF",
//             border: "1px solid #E5E7EB",
//             borderRadius: "12px",
//             boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
//           }}
//         >
//           <CardContent sx={{ padding: "16px" }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="#2B4593"
//               gutterBottom
//               align="center"
//             >
//               Current Visitors Inside
//             </Typography>
//             <Divider sx={{ marginBottom: "16px" }} />

//             <Box sx={{
//               height: "auto",
//               minHeight: 400,
//               maxHeight: 500,
//               "& .MuiDataGrid-root": {
//                 border: "none",
//               },
//               "& .MuiDataGrid-main": {
//                 height: "100%",
//               },
//               "& .MuiDataGrid-virtualScroller": {
//                 minHeight: 200,
//                 overflowY: "auto",
//               },
//               "& .MuiDataGrid-columnHeaders": {
//                 borderBottom: "2px solid #2B4593",
//               },
//               "& .MuiDataGrid-cell": {
//                 borderRight: "1px solid #f0f0f0",
//               },
//             }}>
//               <DataGrid
//                 rows={visitorHistory}
//                 columns={columns}
//                 autoHeight={false}
//                 slots={{
//                   toolbar: GridToolbar,
//                 }}
//                 rowSpacingType="border"
//                 pagination={true}
//                 pageSizeOptions={[5, 10, 25, 50]}
//                 initialState={{
//                   pagination: {
//                     paginationModel: {
//                       pageSize: 5
//                     }
//                   },
//                 }}
//                 slotProps={{
//                   toolbar: {
//                     showQuickFilter: true,
//                   },
//                 }}
//                 getRowId={(row) => row.epc || row.memCd || Math.random()}
//                 sx={{
//                   "& .MuiDataGrid-colCell": {
//                     backgroundColor: "#2B4593",
//                     color: "#fff",
//                     fontSize: 16,
//                     fontWeight: 600,
//                   },
//                 }}
//               />
//             </Box>
//           </CardContent>
//         </Card>
//       </Box>

//       {/* Visitor Details Modal */}
//       <Modal
//         open={openModal}
//         onClose={handleCloseModal}
//         aria-labelledby="visitor-details-modal"
//         aria-describedby="visitor-details-description"
//       >
//         <Box sx={modalStyle}>
//           {selectedVisitor && (
//             <>
//               <Typography id="visitor-details-modal" variant="h6" component="h2" sx={{
//                 mb: 3,
//                 textAlign: "center",
//                 color: "#2B4593",
//                 fontWeight: "bold"
//               }}>
//                 Visitor Details
//               </Typography>

//               <Grid container spacing={4}>
//                 <Grid item xs={12} md={4} sx={{
//                   display: "flex",
//                   flexDirection: "column",
//                   alignItems: "center"
//                 }}>
//                   {renderPhoto(selectedVisitor.memberPhoto, 180)}
//                   <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
//                     {selectedVisitor.name}
//                   </Typography>
//                   <Box
//                     sx={{
//                       backgroundColor:
//                         selectedVisitor.status === "OUT" ? "#E9FDEE" : "#FFF4E6",
//                       color:
//                         selectedVisitor.status === "OUT" ? "#059669" : "#F97316",
//                       padding: "6px 16px",
//                       borderRadius: "12px",
//                       fontWeight: 600,
//                       fontSize: "0.875rem",
//                       width: "fit-content",
//                       mt: 1,
//                       mb: 2
//                     }}
//                   >
//                     {selectedVisitor.status || "IN"}
//                   </Box>
//                 </Grid>

//                 <Grid item xs={12} md={8}>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Member ID:</strong> {selectedVisitor.memCd || "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Mobile:</strong> {selectedVisitor.mobileNo || "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Department:</strong> {selectedVisitor.memDept || "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Type:</strong> {selectedVisitor.memType || "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Visit Date:</strong> {moment(selectedVisitor.vDate).format("DD MMM YYYY")}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>In Time:</strong> {selectedVisitor.inTime ? moment(selectedVisitor.inTime).format("hh:mm A") : "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Out Time:</strong> {selectedVisitor.outTime ? moment(selectedVisitor.outTime).format("hh:mm A") : "N/A"}
//                       </Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={6}>
//                       <Typography variant="body1">
//                         <strong>Time Spent:</strong> {selectedVisitor.spendtime || "N/A"}
//                       </Typography>
//                     </Grid>
//                   </Grid>
//                 </Grid>
//               </Grid>
//             </>
//           )}
//         </Box>
//       </Modal>

//     </Paper>
//   );
// }














